import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw, MessageSquare, MessageSquareReply, Frown } from "lucide-react";
import { Button, Card, Skeleton, SplitView } from "@/components/ui";
import { Review, ReviewFilterParams, ReviewServices } from "@/services/bedrock";
import { useInfiniteQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ReviewListFiltersBar,
  ReviewSummaryCard,
  resolveReviewDatePreset,
  resolveSourceSystemQueryParam,
  type ReviewDatePreset,
} from "@/components/reviews";
import { ReviewFiltersPopover } from "./ReviewFiltersPopover";
import { ReviewInboxListFooter } from "./ReviewInboxListFooter";
import { useReviewFilters } from "../ReviewFiltersContext";
import { useDebounced } from "@/utils";
import { useReviewsState } from "../hooks/useReviewsState";
import { cn } from "@/lib/utils";
import { readSelectedReviewParam } from "@/components/ShareDesignPost/shareSearchParams";

interface ReviewListProps {
  playerId: number | null;
  selectedReview: Review | null;
  setSelectedReview: (review: Review | null) => void;
  onListStateChange?: (isLoading: boolean, reviewsCount: number) => void;
}

export function ReviewList({ playerId, selectedReview, setSelectedReview, onListStateChange }: ReviewListProps) {
  const {
    search,
    setSearch,
    triageFilter,
    setTriageFilter,
    clearFilters,
    sourceSystemFilter,
    ratingTypeFilter,
    preset,
    setPreset,
    date,
    sortFilter,
    orderFilter,
    sentimentFilter,
    setSentimentFilter,
    activeFiltersCount,
    completedReviews,
  } = useReviewFilters();

  const [showExtendedFilters, setShowExtendedFilters] = useState(false);

  // Local state for debounced search input
  const [localSearch, setLocalSearch] = useState(search);
  const debouncedSearch = useDebounced(localSearch, 400);

  // Sync local search when context search changes (e.g. filters cleared)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // Update context search when debounced localSearch changes
  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch, setSearch, search]);

  // Fetch shared unreplied count via useReviewsState hook
  const { unrepliedCount } = useReviewsState();

  // Query for the list of reviews using infinite query
  const {
    data: reviewsResponse,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "reviews-inbox-list-infinite",
      playerId,
      search,
      triageFilter,
      sourceSystemFilter,
      ratingTypeFilter,
      preset,
      date,
      sortFilter,
      orderFilter,
      sentimentFilter,
    ],
    queryFn: ({ pageParam }) => {
      const params: ReviewFilterParams = {
        page: pageParam as number,
        perPage: 15,
        playerId: playerId || undefined,
        search: search || undefined,
      };

      if (triageFilter === "needingReply") {
        params.replied = false;
        params.replySkipped = false;
      }
      if (triageFilter === "replied") params.replied = true;
      if (triageFilter === "skipped") params.replySkipped = true;

      const resolvedSourceSystem = resolveSourceSystemQueryParam(sourceSystemFilter);
      if (resolvedSourceSystem) {
        params.sourceSystem = resolvedSourceSystem;
      }
      if (ratingTypeFilter) {
        params.ratingType = ratingTypeFilter === "review" ? ["star"] : ["nps", "ten_point", "text"];
      }
      if (sentimentFilter) {
        params.sentiment = sentimentFilter;
      }
      if (sortFilter) {
        params.sort = sortFilter;
      }
      if (orderFilter) {
        params.order = orderFilter;
      }

      const datePreset = preset && preset !== "all_time" ? (preset as ReviewDatePreset) : null;
      const range = resolveReviewDatePreset(datePreset, {
        startDate: date?.from ? format(date.from, "yyyy-MM-dd") : null,
        endDate: date?.to ? format(date.to, "yyyy-MM-dd") : null,
      });
      if (range.startDate) params.startDate = range.startDate;
      if (range.endDate) params.endDate = range.endDate;

      return ReviewServices.getReviews(params);
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
  });

  const reviews = useMemo(() => {
    const rawReviews = reviewsResponse?.pages?.flatMap(p => p.content) ?? [];

    // Snappy UX local filter: immediately filter out reviews that have been updated in local state/cache
    // before the React Query query refetch request finishes.
    if (triageFilter === "needingReply") {
      return rawReviews.filter(r => {
        // Keep it visible in the list if it has just been completed/replied in this session!
        if (completedReviews[r.id]) return true;
        return !r.replied && !r.replySkipped;
      });
    }
    if (triageFilter === "replied") {
      return rawReviews.filter(r => r.replied);
    }
    if (triageFilter === "skipped") {
      return rawReviews.filter(r => r.replySkipped);
    }

    return rawReviews;
  }, [reviewsResponse?.pages, triageFilter, completedReviews]);

  const totalReviewsCount = reviewsResponse?.pages?.[0]?.pagination?.totalCount ?? 0;

  // A review opened through ?review= stays selected even when it isn't on the loaded pages (the panel
  // fetches it by id), until another review is picked or the filters change.
  const location = useLocation();
  const urlSelection = useRef<number | null>(readSelectedReviewParam(location.search));
  const filtersKey = JSON.stringify([
    search,
    triageFilter,
    sourceSystemFilter,
    ratingTypeFilter,
    preset,
    date,
    sortFilter,
    orderFilter,
    sentimentFilter,
  ]);
  const initialFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (filtersKey !== initialFiltersKey.current) urlSelection.current = null;
  }, [filtersKey]);

  // Auto-select the first review when the list changes
  useEffect(() => {
    // While a filter change reloads the list it's briefly empty; decide once the new results are in,
    // so a selected review that's still in them stays selected.
    if (isLoading) return;
    if (urlSelection.current != null) {
      if (selectedReview && Number(selectedReview.id) === urlSelection.current) return;
      urlSelection.current = null;
    }
    if (reviews.length > 0) {
      const exists = reviews.some(r => r.id === selectedReview?.id);
      if (!selectedReview || !exists) {
        setSelectedReview(reviews[0]);
      }
    } else {
      setSelectedReview(null);
    }
  }, [reviews, selectedReview, setSelectedReview, isLoading]);

  // Notify parent of state changes defensively to prevent infinite loop on anonymous prop references
  const onListStateChangeRef = React.useRef(onListStateChange);
  useEffect(() => {
    onListStateChangeRef.current = onListStateChange;
  }, [onListStateChange]);

  useEffect(() => {
    if (onListStateChangeRef.current) {
      onListStateChangeRef.current(isLoading, reviews.length);
    }
  }, [isLoading, reviews.length]);

  // Intersection Observer for infinite scrolling
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const listHeader = (
    <div className="p-4 bg-transparent space-y-4">
      {/* Split Priority Cards Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Needs Reply Card */}
        <Card
          onClick={() => {
            if (triageFilter === "needingReply") {
              setTriageFilter(null);
              setPreset("all_time");
            } else {
              setTriageFilter("needingReply");
              if (unrepliedCount > 0) setPreset("last_30_days");
              else setPreset("all_time");
            }
          }}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-2xl cursor-pointer transition-all border-2 select-none gap-1.5",
            triageFilter === "needingReply"
              ? "bg-indigo-50 border-indigo-500 shadow-inner"
              : "bg-white border-slate-100 hover:border-indigo-200 shadow-sm"
          )}
        >
          {unrepliedCount > 0 ? (
            <div className="size-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[11px] font-extrabold shadow-xs">
              {unrepliedCount}
            </div>
          ) : (
            <div className={cn("p-1 rounded-md", triageFilter === "needingReply" ? "bg-indigo-100" : "bg-slate-50")}>
              <MessageSquareReply
                className={cn("size-4", triageFilter === "needingReply" ? "text-indigo-600" : "text-slate-400")}
              />
            </div>
          )}
          <span
            className={cn(
              "text-[10px] font-bold uppercase tracking-wider text-center leading-tight",
              triageFilter === "needingReply" ? "text-indigo-900" : "text-slate-500"
            )}
          >
            {unrepliedCount > 0 ? "Needs Reply (Last 30d)" : "Needs Reply"}
          </span>
        </Card>

        {/* Unhappy Clients Card */}
        <Card
          onClick={() => setSentimentFilter(sentimentFilter === "negative" ? null : "negative")}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-2xl cursor-pointer transition-all border-2 select-none gap-1.5",
            sentimentFilter === "negative"
              ? "bg-rose-50 border-rose-500 shadow-inner"
              : "bg-white border-slate-100 hover:border-rose-200 shadow-sm"
          )}
        >
          <div className={cn("p-1 rounded-md", sentimentFilter === "negative" ? "bg-rose-100" : "bg-slate-50")}>
            <Frown className={cn("size-4", sentimentFilter === "negative" ? "text-rose-600" : "text-slate-400")} />
          </div>
          <span
            className={cn(
              "text-[10px] font-bold uppercase tracking-wider text-center leading-tight",
              sentimentFilter === "negative" ? "text-rose-900" : "text-slate-500"
            )}
          >
            Unhappy Clients
          </span>
        </Card>
      </div>

      <ReviewListFiltersBar
        searchValue={localSearch}
        onSearchChange={value => {
          setLocalSearch(value);
          if (!value) setSearch("");
        }}
        activeFilterCount={activeFiltersCount}
        filterOpen={showExtendedFilters}
        onFilterOpenChange={setShowExtendedFilters}
        filterContent={<ReviewFiltersPopover onOpenChange={setShowExtendedFilters} />}
      />
    </div>
  );

  return (
    <SplitView.List
      header={listHeader}
      footer={<ReviewInboxListFooter playerId={playerId} totalCount={totalReviewsCount} isLoading={isLoading} />}
      bodyClassName="flex flex-col gap-3 pt-3 select-none"
    >
      {isLoading && !isFetchingNextPage ? (
        // Floating Skeletons Cards (Initial Loading Only)
        Array.from({ length: 4 }).map((_, index) => (
          <Card
            key={index}
            className="mx-4 p-3.5 border border-slate-200/80 rounded-xl bg-white shadow-sm shrink-0 flex flex-col gap-2.5 animate-pulse"
          >
            {/* Top Row: Left Identity Column and Right Metrics Column */}
            <div className="flex items-start justify-between gap-3">
              {/* Left Side: Identity & Badge Mock */}
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5 rounded-md shrink-0" />
                  <Skeleton className="h-4 w-28 rounded" />
                </div>
                {/* Badge Mock */}
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>

              {/* Right Side: Rating & Time Mock */}
              <div className="flex flex-col items-end gap-1.5 shrink-0 mt-0.5">
                <Skeleton className="h-4 w-16 rounded" />
                <Skeleton className="h-3 w-10 rounded" />
              </div>
            </div>

            {/* Bottom: Content Preview Mock */}
            <div className="space-y-1.5 mt-0.5">
              <Skeleton className="h-3.5 w-full rounded" />
              <Skeleton className="h-3.5 w-5/6 rounded" />
            </div>
          </Card>
        ))
      ) : reviews.length === 0 ? (
        // Empty State (Flat)
        <div className="mx-4 flex flex-col items-center justify-center text-center p-8 text-slate-400 h-72 animate-in fade-in duration-300 bg-white border border-slate-200 rounded-2xl shadow-sm shrink-0">
          <MessageSquare className="h-7 w-7 text-slate-300 mb-3" />
          <h5 className="font-bold text-sm text-slate-700 mb-1">No reviews found</h5>
          <p className="text-xs text-slate-500 max-w-[200px] leading-relaxed">
            Adjust filters or toggle status to find older reviews.
          </p>
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="mt-4 text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50/50 h-8 px-4 border border-indigo-100"
            >
              Clear Active Filters
            </Button>
          )}
        </div>
      ) : (
        // Floating Review Cards Mapping & Scroll Load Observer
        <div className="px-4 pb-4 flex flex-col gap-3">
          {reviews.map((review: Review) => {
            const isSelected = selectedReview?.id === review.id;

            return (
              <ReviewSummaryCard
                key={review.id}
                review={review}
                isSelected={isSelected}
                onClick={() => setSelectedReview(review)}
              />
            );
          })}

          {/* Infinite scroll loader trigger element */}
          {hasNextPage && (
            <div ref={observerRef} className="py-4 flex items-center justify-center gap-2 text-slate-400 shrink-0">
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-indigo-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Loading more reviews...</span>
            </div>
          )}
        </div>
      )}
    </SplitView.List>
  );
}
